import { debounceTime } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { AmplifyUser } from '@aws-amplify/ui';
import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "../../services/auth.service";
import { Auth } from "aws-amplify";
import { AWSLambda } from "../../AWSLambda.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { SpinnerService } from "../../components/spinner/spinner.service";
import { GlobalDataService } from '../../services/global-data.service';
import { MatSelect } from '@angular/material/select';
import { DecimalPipe } from '@angular/common';

import * as data from '../../../assets/json/countries.json'
import { TransfersService } from './transfers.service';
import { PromoComponent } from '../../components/promo/promo.component';
import { PromoService } from '../../components/promo/promo.service';

/**
 * Interfaz que describe la estructura de datos para países.
 */
interface CountryData {
  [key: string]: {
    [key: string]: {
      "ISO-3": string;
      "Country": string;
    };
  };
}

/**
 * Componente principal para gestionar transferencias.
 * Incluye selección de país, producto, validación KYC, y gestión de promociones.
 */
@Component({
  selector: "app-transfers",
  templateUrl: "./transfers.component.html",
  styles: [],
  providers: [DecimalPipe]
})
export class TransfersComponent implements OnInit, AfterViewInit {

  /**
   * Referencia global a window.
   */
  window = window;

  /** Cantidad a transferir. */
  quantity: number = 1;

  /** Precio final calculado. */
  finalPrice: number = 0;

  /** Condiciones aplicables al usuario/transferencia. */
  conditions: any | null = null;

  /** Transacción para reutilizar datos previamente ingresados. */
  reuseTransaction: any | null = null;

  /** Código del país por defecto. */
  DEFAULT_COUNTRY = 'COL';

  /** Tipo de producto por defecto. */
  DEFAULT_PRODUCT_TYPE = 'DEPOSIT_TO_ACCOUNT';

  /** Método de pago por defecto. */
  DEFAULT_PAYMENT_METHOD = 'PAYMENT_CARD'; //'PAYMENT_ACCOUNT'

  /** Moneda seleccionada para la transferencia. */
  selectedCurrency: string = "EUR";

  /** Referencia al stepper del formulario. */
  @ViewChild(MatStepper) stepper!: MatStepper;

  /** Referencia al select del país (Angular Material). */
  @ViewChild('mySelect') mySelect!: MatSelect;

  /** Usuario autenticado de Amplify. */
  user!: AmplifyUser;

  /** Atributos del usuario (ej. email, país). */
  attributes: any;

  /** Información del país seleccionado. */
  countryinfo: { countryCode: string, country: string } | null = null;

  /** Datos finales que serán enviados para realizar la transferencia. */
  finalData = {};

  /** Flag para primer envío. */
  firstSend: boolean = false;

  /** Flag para indicar si no hay comisión. */
  noFee: boolean = false;

  /** Monto a transferir por defecto. */
  amount = 100;

  /** País del usuario autenticado. */
  userCountry: { countryCode: string, country: string, iso3: string } | null = null;

  /** Flag para indicar si el KYC (verificación) es válido. */
  kycValid: boolean = false;

  /** URL para completar KYC si es requerido. */
  kycUrl: string = '';

  /** ID único para el usuario Revolut. */
  revolutID: string | null = null;

  /** ID del producto seleccionado. */
  selectedProduct: string | null = "";

  /** Email del usuario autenticado o de destino. */
  email: string = "";

  /** Regiones agrupadas por país. */
  regions!: RegionsByCountry;

  /** Listado completo de productos disponibles. */
  products: any[] = [];

  /** Productos filtrados temporalmente por país o tipo. */
  tmpProducts: any[] = [];

  /** Producto seleccionado actualmente. */
  product: any;

  /** Tipos de productos disponibles (ej. depósito, retiro). */
  productTypes: any[] = [];

  /** Tipo de producto seleccionado. */
  selectedProductType: string | null = "";

  /** Productos filtrados tras selección de tipo. */
  filteredProducts: any[] = [];

  /** País seleccionado para la transferencia. */
  selectedCountry: string = "";

  /** Lista de países filtrados para la selección. */
  filteredCountries: any[] = [];

  /** Lista temporal de países. */
  tmpCountries: any[] = [];

  /** Tipos de transacciones (ej. tipo de transferencia). */
  transaction_types: any[] = [];

  /** Patrones fijos para validación de texto. */
  fixedTextPatterns: any[] = [];

  /**
 * Lista de países filtrados con información detallada.
 * Se utiliza para mostrar países disponibles, por ejemplo, en modales de selección de tarifas.
 * Cada elemento incluye:
 * - `countryCode`: Código telefónico del país (ej. +34)
 * - `country`: Nombre del país (ej. España)
 * - `iso3`: Código ISO 3 letras del país (ej. ESP)
 */
  filteredCountriesFull: { countryCode: string, country: string, iso3: string }[] = [];


  /**
   * Objeto con información de la cotización actual,
   * como monto a enviar, comisión, tasa de cambio, etc.
   */
  quotation: {
    quotation_id: number;
    send_amount: number;
    fee: number;
    revenue: number;
    total_due: number;
    exchange_rate: number;
    receive_currency: string;
    receive_amount: number;
    differs_from_requested_amount?: boolean;
    direct_banking_revenue: number;
    card_revenue: number;
  } | null | undefined = null;

  /** Flag que indica si una transferencia está en curso. */
  transferGoing = false;

  /** FormGroup principal para el primer paso del formulario. */
  firstFormGroup: FormGroup;

  /** FormGroup para campos secundarios y validación custom. */
  secondFormGroup: FormGroup = this._formBuilder.group(
    {
      beneficiary_fields: this._formBuilder.group({}),
      account_types: this._formBuilder.group({})
    },
    { validators: this.validateForm }
  );

  /** Estado de spinners de carga, observable para cada spinner. */
  spinnerStates: { [key: string]: Observable<boolean> } = {};

  /** Indica si guardar la tarjeta para uso futuro. */
  save_card: boolean = false;

  /** ID de tarjeta guardada. */
  cardId: string = "";

  /** Método de pago actual seleccionado. */
  payment_method: string = this.DEFAULT_PAYMENT_METHOD;

  /** URL de pago si aplica. */
  paymenturl: string | null = null;

  /** Información y permisos relacionados con Paylands. */
  paylands?: Paylands = { allow_direct: false, cards: [] };

  /** Precio antes de aplicar promociones. */
  priceBeforePromo: number | null = null;

  /** Referencia al componente hijo de promociones. */
  @ViewChild(PromoComponent) promoValue!: PromoComponent;

  /** Objeto con datos de promoción activa. */
  promo: any = null;

  /** Código de promoción seleccionado o ingresado. */
  promoCode: string | null = '';

  /** Flag que indica si hay promociones disponibles. */
  isPromoAvailable = false;

  /** ID del intervalo para refresco o tareas periódicas. */
  intervalId: any;

  /**
   * Constructor del componente.
   *
   * @param authService Servicio de autenticación.
   * @param router Servicio para la navegación entre rutas.
   * @param lambda Cliente AWS Lambda para invocación de funciones.
   * @param snackBar Servicio para mostrar notificaciones tipo snack bar.
   * @param _formBuilder Constructor de formularios reactivos.
   * @param spinnerService Servicio para mostrar/ocultar spinner de carga.
   * @param cdr ChangeDetectorRef para manejar la detección manual de cambios.
   * @param globalDataService Servicio para acceder a datos globales.
   * @param transfersService Servicio para operaciones relacionadas con transferencias.
   * @param promoService Servicio para gestión de promociones.
   */
  constructor(
    private authService: AuthService,
    private router: Router,
    private lambda: AWSLambda,
    private snackBar: MatSnackBar,
    private _formBuilder: FormBuilder,
    private spinnerService: SpinnerService,
    private cdr: ChangeDetectorRef,
    private globalDataService: GlobalDataService,
    private transfersService: TransfersService,
    private promoService: PromoService
  ) {
    this.getRevolutUser = this.getRevolutUser.bind(this);

    /**
     * FormGroup principal para manejar los campos iniciales del formulario.
     */
    this.firstFormGroup = this._formBuilder.group({
      email: ['', [Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/), Validators.required]],
      amount: [0, [Validators.required]],
      selectedCountry: [this.DEFAULT_COUNTRY, [Validators.required]],
      selectedProductType: [this.DEFAULT_PRODUCT_TYPE, [Validators.required]],
      selectedProduct: ['', [Validators.required]],
      quotationId: [0, [Validators.required]],
      send_amount: [0, [Validators.required]],
      fee: [0],
      revenue: [0],
      total_due: [0],
      exchange_rate: [0],
      receive_currency: [''],
      payment_method: [this.DEFAULT_PAYMENT_METHOD]
    });
  }

  /**
   * Método que se ejecuta cuando cambia el paso en el formulario tipo Stepper.
   *
   * @param event Evento del cambio de paso.
   */
  onStepChange(event: any): void {
    if (event.selectedIndex === 2) {
      this.promoValue?.promo.subscribe((valor: any) => {
        this.promo = valor;
        this.promoCode = valor?.promos[0]?.id;
        this.createData();
      });
    }
  }

  /**
   * Método del ciclo de vida Angular que se ejecuta después de que la vista ha sido inicializada.
   * Se usa para ajustar el ancho de un panel.
   */
  ngAfterViewInit(): void {
    this.adjustPanelWidth();
  }

  /**
   * Caché local para evitar múltiples llamadas a la API para obtener el usuario Revolut.
   * @private
   */
  private revolutUserCache: any = null;

  /**
   * Subject para controlar el estado de consulta a la API Revolut.
   * @private
   */
  private isCheckingRevolutUser = new BehaviorSubject<boolean>(false);

  /**
   * Verifica si se pueden evitar comisiones según las condiciones y método de pago.
   */
  async avoidCommisions(): Promise<void> {
    let free_for_method = false;

    if (this.payment_method == "PAYMENT_CARD") {
      free_for_method = this.conditions.allowFreeCard && this.conditions.maxFreeCard >= this.amount;
    } else {
      free_for_method = this.conditions.allowFreeDB && this.conditions.maxFreeDB >= this.amount;
    }

    this.firstSend = this.globalDataService.allowFreeTransfer && free_for_method;
  }

  /**
   * Obtiene la información del usuario Revolut, validando su KYC.
   * Cachea la respuesta para evitar llamadas repetidas.
   * Muestra spinner durante la operación.
   *
   * @returns `null` si válido, objeto con error `{ formInvalid: true }` si no.
   */
  async getRevolutUser(): Promise<null | { formInvalid: boolean }> {
    if (this.isCheckingRevolutUser.value) {
      return this.revolutUserCache?.valid_kyc ? null : { formInvalid: true };
    }

    if (this.revolutUserCache) {
      return this.revolutUserCache.valid_kyc ? null : { formInvalid: true };
    }

    this.isCheckingRevolutUser.next(true);
    this.spinnerService.show('spinnerProfile');

    const countrycode = this.findCountry(this.attributes?.['custom:country'] ?? 'ES', 'countryCode');
    this.revolutUserCache = await this.transfersService.createOrUpdateRevoluUser(this.revolutID, this.attributes, this.email, countrycode);

    this.kycValid = this.revolutUserCache?.valid_kyc === true ?? false;
    this.kycUrl = this.revolutUserCache?.valid_kyc !== true ? this.revolutUserCache?.valid_kyc : '';

    if (this.revolutUserCache && this.revolutUserCache.document_country != null) {
      this.userCountry = this.findCountry(this.revolutUserCache.document_country, 'countryCode');
    } else {
      this.userCountry = this.findCountry('ES', 'countryCode');
    }

    if (!this.kycValid) {
      this.revolutUserCache = null;
    } else {
      clearInterval(this.intervalId);
    }

    this.isCheckingRevolutUser.next(false);
    this.spinnerService.hide('spinnerProfile');

    return this.kycValid ? null : { formInvalid: true };
  }

  /**
   * Valida que los campos 'beneficiary_fields' y 'account_types' sean válidos.
   *
   * @param group FormGroup a validar.
   * @returns `null` si válido, objeto con error si inválido.
   */
  validateForm(group: AbstractControl): { [key: string]: any } | null {
    const beneficiaryFieldsValid = group.get('beneficiary_fields')?.valid;
    const accountTypesValid = group.get('account_types')?.valid;

    return beneficiaryFieldsValid && accountTypesValid ? null : { formInvalid: true };
  }

  /**
   * Recibe un mensaje con un FormGroup para actualizar el formulario secundario.
   * Si el control existe en `secondFormGroup`, lo actualiza y fuerza la validación.
   *
   * @param $event Objeto con clave y valor (FormGroup).
   */
  receiveMessage($event: { key: string, value: FormGroup }): void {
    if (this.secondFormGroup.contains($event.key)) {
      this.secondFormGroup.setControl($event.key, $event.value);

      if (this.reuseTransaction) {
        let patchObject: { [key: string]: any } = {};
        if ($event.key == "beneficiary_fields") {
          for (const item of this.reuseTransaction.beneficiary_fields) {
            patchObject[item.name] = item.value;
          }
        } else if ($event.key == "account_types") {
          for (const item of this.reuseTransaction.account_fields) {
            patchObject[item.name] = item.value;
          }
        }
        this.secondFormGroup.get($event.key)?.patchValue(patchObject);

        // Forzar validación
        this.secondFormGroup.get($event.key)?.markAllAsTouched();
        this.secondFormGroup.get($event.key)?.updateValueAndValidity();
      }

    } else {
      console.error(`El control '${$event.key}' no existe en secondFormGroup`);
    }

    if (this.secondFormGroup.valid) {
      this.createData();
    }
  }

  /**
   * Actualiza el validador máximo para el campo 'amount' según método de pago y condiciones.
   */
  updateMaxAmountValidator(): void {
    const control = this.firstFormGroup.get('amount');
    if (!control) return;

    const newMax = this.payment_method === 'PAYMENT_CARD' ? this.conditions.maxCard : this.conditions.maxDB;
    const validators = [Validators.required, Validators.max(newMax)];

    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  /**
   * Acción a ejecutar cuando se selecciona un país.
   * Actualiza las condiciones, productos y valida si hay productos disponibles.
   */
  async onCountrySelect(): Promise<void> {
    this.countryinfo = this.findCountry(this.selectedCountry);
    const cond_tmp = this.filteredCountries.filter((item: { country: any; }) => item.country == this.selectedCountry);
    this.conditions = cond_tmp[0].conditions;

    this.tmpProducts = this.products.filter(item =>
      item.country === this.selectedCountry &&
      (
        (item.denied_issuer_countries.length === 0 && item.allowed_issuer_countries.length === 0) ||
        (
          (item.denied_issuer_countries.length === 0 || !item.denied_issuer_countries.includes(this.userCountry?.iso3)) &&
          (item.allowed_issuer_countries.length === 0 || item.allowed_issuer_countries.includes(this.userCountry?.iso3))
        )
      )
    );

    if (this.tmpProducts.length === 0) {
      this.snackBar.open("No existen productos disponibles para este país.", 'Close', { duration: 5000 });
    }

    this.productTypes = Array.from(new Set(this.tmpProducts.map(item => item.transaction_type_code)));

    if (this.productTypes.length > 0) {
      this.firstFormGroup.get('selectedProductType')?.enable();
    } else {
      this.firstFormGroup.get('selectedProductType')?.disable();
    }

    // Reset selects y productos
    this.firstFormGroup.patchValue({ selectedProductType: null });
    this.filteredProducts = [];

    if (this.filteredProducts.length > 0) {
      this.firstFormGroup.get('selectedProduct')?.enable();
    } else {
      this.firstFormGroup.get('selectedProduct')?.disable();
    }

    this.product = null;
    this.selectedProduct = null;
    this.selectedProductType = null;
    this.quotation = null;
  }

  /**
   * Acción a ejecutar cuando se selecciona un tipo de producto.
   * Filtra los productos y habilita/deshabilita el selector de producto.
   */
  async onProductTypeSelect(): Promise<void> {
    this.filteredProducts = this.tmpProducts.filter(item =>
      item.country === this.selectedCountry && item.transaction_type_code === this.selectedProductType);

    if (this.filteredProducts.length > 0) {
      this.firstFormGroup.get('selectedProduct')?.enable();
    } else {
      this.firstFormGroup.get('selectedProduct')?.disable();
    }
    this.product = null;
    this.quotation = null;
  }

  /**
   * Acción a ejecutar cuando se selecciona un producto.
   * Si cambia el producto seleccionado, actualiza el formulario.
   */
  async onProductSelect(): Promise<void> {
    if (this.product?.id !== this.selectedProduct) {
      this.product = this.tmpProducts.find(item => item.id === this.selectedProduct);
      this.changeProduct(this.product);
    }
  }

  /**
   * Limpia un array eliminando elementos duplicados basados en sus campos 'name' y 'regex'.
   *
   * @param data Array de objetos a limpiar.
   * @returns Array filtrado sin duplicados.
   */
  cleanArray<T extends { fields?: { name: string; regex: string }[]; name?: string; regex?: string }>(data: T[]): T[] {
    const seen = new Set();

    return data.filter(item => {
      let key: string;

      if (item.fields) {
        key = item.fields.map(f => `${f.name}:${f.regex}`).join("|");
      } else if (item.name && item.regex) {
        key = `${item.name}:${item.regex}`;
      } else {
        return true;
      }

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }

  /**
   * Cambia el producto seleccionado y actualiza el formulario con los nuevos campos y validadores.
   *
   * @param newProduct Producto nuevo seleccionado.
   */
  async changeProduct(newProduct: any): Promise<void> {
    if (!newProduct) return;

    this.updateAmountValidators(newProduct);

    this.resetFormGroup('beneficiary_fields');
    this.resetFormGroup('account_types');

    const cleanedBeneficiaries = this.cleanArray(newProduct.beneficiary_fields);
    const cleanedAccounts = this.cleanArray(newProduct.account_types);

    // Espera a la próxima ejecución del ciclo para evitar errores de timing
    await Promise.resolve();

    this.product = {
      ...newProduct,
      beneficiary_fields: cleanedBeneficiaries,
      account_types: cleanedAccounts
    };

    const amountControl = this.firstFormGroup.get('amount');
    if (amountControl && amountControl.valid) {
      this.updateQuotation();
    }
  }

  /**
   * Actualiza los validadores para el campo 'amount' cuando cambia el producto.
   *
   * @param product Producto con nuevas reglas de validación.
   * @private
   */
  private updateAmountValidators(product: any): void {
    const amountControl = this.firstFormGroup.get('amount');
    if (amountControl) {
      amountControl.setValidators([Validators.required]);
      amountControl.updateValueAndValidity();
    }
  }

  /**
   * Reinicia un FormGroup hijo dentro de 'secondFormGroup' eliminando todos sus controles.
   * @param formGroupName - nombre del FormGroup a resetear dentro de secondFormGroup
   */
  resetFormGroup(formGroupName: string) {
    const fg = this.secondFormGroup.get(formGroupName);
    if (fg && fg instanceof FormGroup) {
      // Recorre todos los controles hijos y los elimina uno a uno
      Object.keys(fg.controls).forEach(control => {
        fg.removeControl(control);
      });
    }
  }

  /**
   * Imprime en consola de forma recursiva todos los controles dentro de un FormGroup.
   * @param group - FormGroup que se va a recorrer
   * @param parentKey - cadena que representa el prefijo de la ruta del control (para controles anidados)
   */
  printFormGroupControls(group: FormGroup, parentKey: string = ''): void {
    for (const key of Object.keys(group.controls)) {
      const control = group.get(key);
      // Construir el path completo para el control actual
      const fullPath = parentKey ? `${parentKey}.${key}` : key;

      if (control instanceof FormGroup) {
        // Si el control es otro FormGroup, llamar recursivamente para imprimir sus controles
        this.printFormGroupControls(control, fullPath);
      } else {
        // Si es un FormControl, imprimir su nombre completo en consola
        console.log(`Campo: ${fullPath}`);
      }
    }
  }

  /**
   * Avanza al siguiente paso del stepper.
   */
  nextstep() { this.stepper.next(); }

  /**
   * Retrocede al paso anterior del stepper.
   */
  prevstep() { this.stepper.previous(); }

  /**
   * Método del ciclo de vida Angular, detecta cambios en la vista.
   * Se usa para evitar errores de detección de cambios con ChangeDetectorRef.
   */
  ngAfterViewChecked() { this.cdr.detectChanges(); }

  /**
   * Función para enviar la transferencia.
   * Muestra spinner de carga y llama a la función que obtiene la URL de pago.
   */
  async submitTransfer() {
    this.transferGoing = true;
    this.spinnerService.show('fullpage');
    await this.getPaymentURL(this);
    this.spinnerService.hide('fullpage');
  }

  /**
   * Evento keyup en input para filtrar países.
   * Reinicia la lista filtrada y llama a búsqueda.
   */
  onKey(event: Event) {
    this.filteredCountries = [];
    this.search((event.target as HTMLInputElement).value);
  }

  /**
   * Filtra la lista de países según el valor introducido.
   * Si el valor está vacío, resetea la selección.
   * @param value - texto introducido para filtrar países
   */
  search(value: string) {
    if (value == '') {
      this.onCountrySelect();
    } else {
      let filter = value.toLowerCase();
      for (let i = 0; i < this.filteredCountriesFull.length; i++) {
        let option = this.tmpCountries[i];
        // Si el nombre del país contiene el texto filtrado, agregarlo a la lista filtrada
        if (option.country.toLowerCase().indexOf(filter) >= 0) {
          this.filteredCountries.push(option);
        }
      }
    }
  }

  /**
   * Ciclo de vida OnInit de Angular, se ejecuta al iniciar el componente.
   * - Inicializa variables
   * - Carga datos de usuario, países, productos, condiciones
   * - Configura suscripciones a cambios de formulario
   */
  async ngOnInit() {
    this.transferGoing = false;

    // Consultar si hay promociones activas para "transfers"
    this.isPromoAvailable = await this.promoService.isPromosActive("transfers");

    // Obtener datos de una posible transacción reutilizada guardada en sesión
    let transactionData = sessionStorage.getItem('transactionData');
    this.reuseTransaction = transactionData ? JSON.parse(transactionData) : null;
    if (this.reuseTransaction) {
      sessionStorage.removeItem('transactionData');
      transactionData = null;
    }

    // Inicializar estados de spinners
    this.spinnerStates['fullpage'] = this.spinnerService.getSpinnerState('fullpage');
    this.spinnerStates['spinnerProfile'] = this.spinnerService.getSpinnerState('spinnerProfile');
    this.spinnerStates['spinnerCountries'] = this.spinnerService.getSpinnerState('spinnerCountries');
    this.spinnerStates['spinnerQuot'] = this.spinnerService.getSpinnerState('spinnerQuot');

    this.spinnerService.show('spinnerProfile');
    this.spinnerService.show('spinnerCountries');

    try {
      // Refrescar sesión y obtener usuario autenticado
      await this.authService.refresh();
      this.user = await Auth.currentAuthenticatedUser();
      // Obtener detalles del usuario desde Lambda
      this.attributes = await this.lambda.getLambdaFunction("lambda", "/get/userDetails");

      // Asignar propiedades relevantes
      this.revolutID = this.attributes.revolut_id ?? null;
      this.kycValid = this.attributes.kyc_validated == "1" ?? false;
      this.email = this.attributes.email ?? '';

      // Obtener datos relacionados con Paylands
      this.paylands = await this.transfersService.getPaylands(this.revolutID ?? '0');
    } catch (error) {
      this.authService.logout(); // Si falla, salir del sistema
    }

    // Si no hay RevolutID o KYC válido, obtener usuario Revolut
    if (!this.revolutID || !this.kycValid) {
      await this.getRevolutUser();
    }

    // Cargar datos globales de la app para enviar dinero
    let response = this.globalDataService.sendMoneyData;
    this.filteredCountries = response.countries;
    this.updateFilteredCountries(response.countries.map((item: { country: any; }) => item.country));
    this.transaction_types = response.transaction_types;
    this.products = response.products;
    this.regions = response.regions;

    // Buscar la info del país por defecto
    this.countryinfo = this.findCountry(this.DEFAULT_COUNTRY);
    const cond_tmp = this.filteredCountries.filter((item: { country: any; }) => { return item.country == this.DEFAULT_COUNTRY });
    this.conditions = cond_tmp[0].conditions;

    // Suscribirse a cambios del campo 'amount' para actualizar reglas y promociones
    this.firstFormGroup.get('amount')?.valueChanges
      .pipe(debounceTime(500))
      .subscribe(value => {
        if (value !== null && value !== undefined && value > 0 && this.product) {
          this.noFee = this.firstSend && value <= 1000;
        }
      });

    // Suscribirse a cambios del campo 'email' para validar y actualizar usuario Revolut
    this.firstFormGroup.get('email')?.valueChanges.subscribe(value => {
      const emailControl = this.firstFormGroup.get('email');
      if (emailControl && emailControl.valid) {
        this.email = value;
        this.getRevolutUser();
      }
    });

    // Suscribirse a cambios de país seleccionado para actualizar filtros y validadores
    this.firstFormGroup.get('selectedCountry')?.valueChanges.subscribe(value => {
      if (value !== null && value !== undefined) {
        this.selectedCountry = value;
        this.onCountrySelect();
        this.updateMaxAmountValidator();
      }
    });

    // Suscribirse a cambios de tipo de producto seleccionado
    this.firstFormGroup.get('selectedProductType')?.valueChanges.subscribe(value => {
      if (value !== null && value !== undefined) {
        this.selectedProductType = value;
        this.onProductTypeSelect();
      }
    });

    // Suscribirse a cambios del producto seleccionado
    this.firstFormGroup.get('selectedProduct')?.valueChanges.subscribe(value => {
      if (value !== null && value !== undefined) {
        this.selectedProduct = value;
        this.onProductSelect();
      }
    });

    // Suscribirse a cambios en el método de pago para actualizar validadores y cálculos
    this.firstFormGroup.get('payment_method')?.valueChanges.subscribe(value => {
      if (value !== null && value !== undefined) {
        this.payment_method = value;
        this.updateMaxAmountValidator();
        this.updateRevenue();
        this.avoidCommisions();
      }
    });

    // Inicializar el campo email con el valor obtenido en atributos
    this.firstFormGroup.patchValue({ email: this.attributes.email ?? '' });

    // Si no hay transacción reutilizada, usar valores por defecto para país, producto y monto
    if (!this.reuseTransaction) {
      this.firstFormGroup.patchValue({ selectedCountry: this.DEFAULT_COUNTRY });
      this.firstFormGroup.patchValue({ selectedProductType: this.DEFAULT_PRODUCT_TYPE });
      this.firstFormGroup.patchValue({ amount: this.amount }, { emitEvent: true });
      this.product = this.products.find(
        item => item.country === this.DEFAULT_COUNTRY && item.transaction_type_code === this.DEFAULT_PRODUCT_TYPE
      );
    } else {
      // Si hay transacción reutilizada, recuperar datos y setear formulario
      let transactionCountry = this.findCountry(this.reuseTransaction.country, 'country');
      this.firstFormGroup.patchValue({ selectedCountry: transactionCountry?.iso3 });
      this.product = this.products.find(
        item => item.country === transactionCountry?.iso3 && item.id == this.reuseTransaction.quotation?.product_id
      );
      this.firstFormGroup.patchValue({ selectedProductType: this.product.transaction_type_code });
      this.product = this.products.find(
        item => item.country === transactionCountry?.iso3 && item.id == this.reuseTransaction.quotation?.product_id
      );
      this.firstFormGroup.patchValue({ amount: this.reuseTransaction.quotation?.send_amount }, { emitEvent: true });
    }

    // Setear producto seleccionado en el formulario y aplicar cambios
    this.firstFormGroup.patchValue({ selectedProduct: this.product.id });
    await this.changeProduct(this.product);

    // Obtener patrones regex únicos de campos de beneficiario para validaciones
    const uniqueRegex = Array.from(
      new Set(
        this.products.flatMap(product =>
          product.beneficiary_fields.map((field: { regex: any; }) => field.regex)
        )
      )
    );

    // Filtrar solo patrones con operador '|' y sin sub-patrones complejos
    this.fixedTextPatterns = uniqueRegex.filter(pattern => {
      return pattern.includes('|') && !pattern.includes('(?:') && !pattern.includes('\\d') && !pattern.includes('\\p{L}');
    });

    this.spinnerService.hide('spinnerCountries');
  }

  /**
   * Actualiza el ingreso/revenue según el método de pago y condiciones del país.
   */
  updateRevenue() {
    if (this.quotation) {
      // Buscar el rango de ingresos según el método de pago (tarjeta o banca directa)
      const conditionsToSearch = this.payment_method == 'PAYMENT_CARD' ? this.conditions.revenueCard : this.conditions.revenueDB;
      let matched = conditionsToSearch.find((item: { min: number; max: number; }) => this.amount >= item.min && this.amount <= item.max);

      if (!matched) {
        // si no se ha encontrado ningún rango, usar el de mayor max
        matched = conditionsToSearch.reduce((prev: any, cur: any) =>
          prev.max > cur.max ? prev : cur
        );
      }

      // Calcular el revenue según si la unidad es porcentaje o cantidad fija
      this.quotation.revenue = matched.unit == '%' ? this.quotation.send_amount * matched.quantity / 100 : matched.quantity;
    }
  }

  /**
   * Actualiza la cotización solicitando al backend una nueva con los datos actuales.
   * Actualiza los campos del formulario con la nueva información.
   */
  async updateQuotation() {
    this.spinnerService.show('spinnerQuot');

    // Limpiar cotización y campos relacionados antes de actualizar
    this.quotation = null;
    this.firstFormGroup.patchValue({
      quotationId: null,
      send_amount: null,
      fee: null,
      revenue: null,
      total_due: null,
      exchange_rate: null,
      receive_currency: null,
      receive_amount: null,
      payment_method: this.payment_method,
      direct_banking_revenue: 0,
      card_revenue: 0
    });

    // Obtener nueva cotización
    this.quotation = await this.getQuotation();
    this.updateRevenue();

    if (!this.quotation) {
      this.spinnerService.hide('spinnerQuot');
      console.warn('No quotation received');
      return;
    }

    // Actualizar monto y formato sin emitir eventos para evitar loops
    const newAmount = this.quotation.send_amount;
    this.amount = newAmount;
    this.firstFormGroup.get('amount')?.patchValue(newAmount, { emitEvent: false });
    this.formattedAmount = this.formatCurrency(newAmount);

    // Actualizar formulario con los datos de la nueva cotización
    this.firstFormGroup.patchValue({
      quotationId: this.quotation.quotation_id,
      send_amount: this.quotation.send_amount,
      fee: this.quotation.fee,
      revenue: this.quotation.revenue,
      total_due: this.quotation.total_due,
      exchange_rate: this.quotation.exchange_rate,
      receive_currency: this.quotation.receive_currency ?? '',
      payment_method: this.payment_method,
      card_revenue: this.quotation.card_revenue,
      direct_banking_revenue: this.quotation.direct_banking_revenue
    });

    // Si se está reutilizando una transacción, crear datos para envío
    if (this.reuseTransaction) { this.createData(); }

    this.spinnerService.hide('spinnerQuot');
  }

  // Variables para manejar edición y formato del monto enviado
  isEditing: boolean = false;
  formattedAmount: string = '';
  private originalAmount: number | null = this.amount;

  /**
   * Actualiza el formato de moneda visible según el valor actual del campo 'amount'.
   */
  onAmountInput() {
    const value = this.firstFormGroup.get('amount')?.value;
    this.formattedAmount = this.formatCurrency(value);
  }

  /**
   * Comienza la edición del monto:
   * - Activa el modo edición
   * - Guarda el valor original
   * - Enfoca el input del campo 'amount'
   */
  editAmount() {
    this.isEditing = true;
    this.originalAmount = this.firstFormGroup.get('amount')?.value ?? null;
    setTimeout(() => {
      const input = document.querySelector('input[formControlName="amount"]') as HTMLInputElement;
      input?.focus();
    });
  }


  /**
 * Detiene la edición del campo amount y actualiza la cotización
 * solo si el valor cambió respecto al original.
 */
  stopEditing() {
    this.isEditing = false;
    const currentAmount = this.firstFormGroup.get('amount')?.value ?? null;

    if (currentAmount !== this.originalAmount) {
      this.amount = currentAmount;
      this.updateQuotation();  // Solo si cambió
    }
  }

  /**
   * Formatea un número como moneda en formato español (EUR).
   * @param value Número a formatear
   * @returns String con valor formateado o cadena vacía si es inválido
   */
  formatCurrency(value: number): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  }

  /**
   * Obtiene la cotización de la transferencia para el producto y monto actuales.
   * @returns Un objeto con detalles de la cotización o null si no válido
   */
  async getQuotation(): Promise<{
    quotation_id: number;
    send_amount: number;
    fee: number;
    revenue: number;
    total_due: number;
    exchange_rate: number;
    receive_currency: string;
    receive_amount: number;
    differs_from_requested_amount: boolean;
    card_revenue: number;
    direct_banking_revenue: number;
  } | null | undefined> {
    if (this.isValidAmount && this.product) {
      try {
        return this.transfersService.getQuotation(this.product.id, this.firstFormGroup.controls['amount'].value, this.selectedCurrency);
      } catch (error) {
        // Manejo de error, aquí podría loguearse
      }
    }
    return null;
  }

  /**
   * Obtiene la URL de pago llamando al servicio externo con parámetros actuales.
   * @param _this Contexto del componente (usado para acceder a variables y servicios)
   * @returns URL de pago o false si hay error
   */
  async getPaymentURL(_this: any) {
    try {
      const results = await _this.transfersService.getPaymentURL(
        _this.attributes, _this.finalData, _this.payment_method,
        _this.cardId, _this.save_card, _this.countryinfo.country
      );
      if (!results) {
        _this.snackBar.open("Se ha producido un error durante la transacción. Inténtelo de nuevo más tarde.", 'Close', { duration: 5000 });
      }
      return results;
    } catch (error) {
      console.error('Error calling Lambda function:', error);
    }
    return false;
  }

  /**
   * Valida que el monto ingresado en el formulario sea válido (mayor o igual a cero
   * y que el control esté modificado o tocado)
   * @returns true si válido, false si no
   */
  get isValidAmount(): boolean {
    const control = this.firstFormGroup?.controls['amount'];
    const isInitialValid = control && (control.value > 0 || control.value === 0);
    return isInitialValid && (control?.dirty || control?.touched || control?.value > 0);
  }

  /**
   * Crea el objeto final de datos para enviar, combinando los campos del beneficiario,
   * cuenta, y aplicando descuentos de promociones si hay.
   */
  createData() {
    let beneficiary_fields: DUPLA[] | null = null;
    let account_fields: DUPLA[] | null = null;
    let account: { id: number; fields: DUPLA[] } | null = null;

    const beneficiaryFieldsForm: FormGroup | null = this.secondFormGroup.get('beneficiary_fields') as FormGroup | null;
    const accountTypesForm: FormGroup | null = this.secondFormGroup.get('account_types') as FormGroup | null;

    if (beneficiaryFieldsForm) {
      beneficiary_fields = Object.keys(beneficiaryFieldsForm.controls).map(
        key => new DUPLA(key, beneficiaryFieldsForm.get(key)?.value)
      );
    }

    if (accountTypesForm) {
      account_fields = Object.keys(accountTypesForm.controls).map(
        key => new DUPLA(key, accountTypesForm.get(key)?.value)
      );
    }

    if (this.product?.account_types?.length > 0) {
      account = {
        id: this.product.account_types[0].id,
        fields: account_fields ?? [],
      };
    }

    let promodiscount = 0;
    this.priceBeforePromo = null;
    this.finalPrice = parseFloat(
      this.firstSend
        ? this.firstFormGroup.get('send_amount')?.value
        : this.firstFormGroup.get('total_due')?.value + this.firstFormGroup.get('revenue')?.value
    );

    const promo = this.promo?.promos[0];

    if (promo) {
      const discountNum = this.quantity < promo.quantity || !promo.quantity || promo.quantity == 0 ? this.quantity : promo.quantity;
      promodiscount = promo.unit == "" ? promo.discount * discountNum : parseFloat(this.firstFormGroup.get('revenue')?.value) * discountNum * promo.discount / 100;
      this.priceBeforePromo = this.finalPrice;
    }

    this.finalPrice -= promodiscount;

    this.finalData = {
      userId: this.revolutID,
      quotationId: this.firstFormGroup.get('quotationId')?.value,
      paymentAmount: this.finalPrice,
      beneficiary_fields: beneficiary_fields,
      account: account,
    };

    console.log(this.finalData);
  }

  /**
   * Ajusta el ancho del panel desplegable del MatSelect tras abrirlo.
   * @param select Componente MatSelect que se abrió
   */
  onOpenChange(select: MatSelect) {
    setTimeout(() => {
      this.adjustPanelWidth();
    }, 20); // Tiempo para que el panel haya abierto el DOM
  }

  /** Ajusta el ancho del panel overlay (dropdown) del MatSelect a 200px */
  private adjustPanelWidth() {
    const panel = document.querySelector('.cdk-overlay-pane') as HTMLElement;
    if (panel) {
      panel.style.width = '200px';
    }
  }

  /**
   * Busca datos de país a partir de un valor que puede ser código ISO3,
   * código país o nombre de país.
   * @param value Valor para buscar (ISO3, código país, o nombre)
   * @param searchBy Tipo de búsqueda ('iso3' | 'countryCode' | 'country')
   * @returns Datos del país o null si no encontrado
   */
  findCountry(
    value: string,
    searchBy: 'iso3' | 'countryCode' | 'country' = 'iso3'
  ): { countryCode: string; country: string; iso3: string } | null {
    const countries_info: CountryData = data;

    for (const key in countries_info) {
      for (const countryCode in countries_info[key]) {
        const info = countries_info[key][countryCode];

        if (searchBy === 'iso3' && info['ISO-3'] === value) {
          return { countryCode: countryCode.toLowerCase(), country: info['Country'], iso3: value };
        }

        if (searchBy === 'countryCode' && countryCode.toLowerCase() === value.toLowerCase()) {
          return { countryCode: countryCode.toLowerCase(), country: info['Country'], iso3: info['ISO-3'] };
        }

        if (searchBy === 'country' && info['Country'].toLowerCase() === value.toLowerCase()) {
          return { countryCode: countryCode.toLowerCase(), country: info['Country'], iso3: info['ISO-3'] };
        }
      }
    }
    return null;
  }

  /**
   * Actualiza la lista de países filtrados a partir de una lista de códigos ISO3.
   * @param iso3List Lista de códigos ISO3
   */
  updateFilteredCountries(iso3List: string[]) {
    this.filteredCountriesFull = iso3List
      .map(iso3 => this.findCountry(iso3))
      .filter((data): data is { countryCode: string; country: string; iso3: string } => !!data);
  }

  /**
   * Obtiene la URL del logo de una tarjeta dado su brand (marca).
   * @param brand Marca de la tarjeta (e.g., visa, mastercard)
   * @returns URL a un icono SVG o cadena vacía si no definida
   */
  getCardLogo(brand: string | undefined): string {
    if (!brand) return '';
    return `https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/${brand.toLowerCase()}.svg`;
  }

  /**
   * Abre la ventana de validación KYC y lanza un temporizador que
   * periódicamente verifica el estado del usuario Revolut.
   */
  checkKycValidity() {
    console.log('Abriendo validador KYC y empezando temporizador');
    window.open(this.kycUrl);
    this.intervalId = setInterval(this.getRevolutUser, 10000);

    // Detiene el temporizador después de 120 segundos
    setTimeout(() => {
      clearInterval(this.intervalId);
      console.log("Detenido después de 120 segundos");
    }, 10000 * 12);
  }



}
/**
 * Clase que representa un par nombre-valor genérico.
 * Útil para almacenar datos clave-valor donde el valor puede ser de cualquier tipo.
 */
class DUPLA<T = any> {
  /** Nombre o clave del par */
  name: string;

  /** Valor asociado al nombre */
  value: T;

  /**
   * Crea una nueva instancia de DUPLA
   * @param name Nombre o clave del par
   * @param value Valor asociado al nombre
   */
  constructor(name: string, value: T) {
    this.name = name;
    this.value = value;
  }
}

/**
 * Representa una tarjeta bancaria o de pago.
 */
interface Card {
  /** Identificador único de la tarjeta */
  id: string;

  /** Últimos 4 dígitos de la tarjeta */
  last4: string;

  /** Marca o tipo de tarjeta (e.g., Visa, MasterCard) */
  card_brand: string;

  /** Bin (Bank Identification Number) de la tarjeta */
  bin: string;

  /** Nombre del banco emisor */
  bank_name: string;
}

/**
 * Información sobre métodos de pago y tarjetas permitidas.
 */
interface Paylands {
  /** Indica si se permite el pago directo */
  allow_direct: boolean;

  /** Lista de tarjetas asociadas */
  cards: Card[];
}

/**
 * Representa una región administrativa o geográfica.
 */
export interface Region {
  /** Identificador único de la región */
  id: number;

  /** Identificador del país al que pertenece la región */
  country_id: number;

  /** Nombre de la región */
  name: string;

  /** Código identificador de la región */
  code: string;
}

/**
 * Mapa que relaciona códigos de país con listas de regiones.
 * La clave es el código del país, el valor es un array de regiones.
 */
export interface RegionsByCountry {
  [countryCode: string]: Region[];
}
